import { Directory, File } from "@-/com.npmjs.expo-file-system";
import { defaultDatabaseDirectory } from "@-/com.npmjs.expo-sqlite";
import { hasTursoReplicaOrigin, TURSO_REPLICA_FILE_SUFFIXES } from "#assert-turso-replica-origin";
import { resolveSqlitePath, toFileUri } from "#resolve-sqlite-path";
import { nativeDatabaseGenerations } from "#native-database-generations";

/** Transfer logical roles atomically; no main file or sidecar is renamed. */
export function relocateTursoDatabaseFiles(
  fromDatabaseName: string,
  toDatabaseName: string,
): string[] {
  return nativeDatabaseGenerations.handoff(fromDatabaseName, toDatabaseName)
    ? [fromDatabaseName]
    : [];
}

/** Includes sidecar-only files left by an interrupted guest handoff. */
export async function hasLocalTursoDatabase(databaseName: string): Promise<boolean> {
  if (!hasTursoDatabaseFiles(databaseName)) {
    return false;
  }
  const physical = nativeDatabaseGenerations.peek(databaseName);
  if (physical === null) {
    return false;
  }
  const metadata = new File(`${toFileUri(resolveSqlitePath(physical))}-info`);
  return !hasTursoReplicaOrigin(metadata.exists ? await metadata.text() : null);
}

/** The absolute path of the file currently serving `<name>`, or null when none is allocated. */
export function resolveTursoDatabasePath(databaseName: string): string | null {
  const physical = nativeDatabaseGenerations.peek(databaseName);
  return physical === null ? null : resolveSqlitePath(physical);
}

/** Whether `<name>` (or any of its sidecars) exists on disk. */
export function hasTursoDatabaseFiles(databaseName: string): boolean {
  const physical = nativeDatabaseGenerations.peek(databaseName);
  if (physical === null) {
    return false;
  }
  const databaseUri = toFileUri(resolveSqlitePath(physical));
  // An unreadable path is not evidence of an empty database safe to bootstrap.
  return TURSO_REPLICA_FILE_SUFFIXES.some((suffix) => new File(`${databaseUri}${suffix}`).exists);
}

/** The names in the SQLite directory — for logs around a stash, never for logic. */
export function listTursoDatabaseDirectory(): string[] {
  try {
    return new Directory(toFileUri(defaultDatabaseDirectory))
      .list()
      .map((entry) => entry.name)
      .sort();
  } catch (error) {
    return [`<unreadable: ${error instanceof Error ? error.message : String(error)}>`];
  }
}
